import * as I from 'immutable';

import {api} from 'app/modules/Remote';
import type {ApiOrderableSceneKey} from 'app/modules/Remote/api';
import {ApiFeature, ApiOrderableScene} from 'app/modules/Remote/Feature';

export type OrderableScenes = I.List<I.ImmutableOf<ApiOrderableScene>>;

// The API's largest page, MAX_PER_PAGE in backend/api/common/request_utils.py.
const FEATURES_PER_REQUEST = 100;

export const sceneKeyOf = ({featureId, sourceId, sensingTime}: ApiOrderableSceneKey) =>
  `${featureId}|${sourceId}|${sensingTime}`;

/**
 * The scenes to look up for these cart items: each item's own, plus the truecolor scene a
 * multispectral one borrows its thumbnail and preview from.
 */
export function sceneKeysFor(items: readonly ApiOrderableSceneKey[]): ApiOrderableSceneKey[] {
  const byKey = new Map<string, ApiOrderableSceneKey>();
  for (const {featureId, sourceId, sensingTime} of items) {
    for (const source of [sourceId, sourceId.replace('-MULTISPECTRAL', '')]) {
      const key = {featureId, sourceId: source, sensingTime};
      byKey.set(sceneKeyOf(key), key);
    }
  }
  return [...byKey.values()];
}

export interface SceneLookup {
  /** The keys asked about, so a caller can tell a scene that isn't listed from one never asked for. */
  asked: ReadonlySet<string>;
  scenesByFeature: ReadonlyMap<number, OrderableScenes>;
}

export async function lookUpScenes(keys: ApiOrderableSceneKey[]): Promise<SceneLookup> {
  const scenesByFeature = new Map<number, OrderableScenes>();
  if (keys.length) {
    const listed = (await api.imagery.orderableScenes.lookUp(keys)).get('data');
    listed.forEach((scene) => {
      if (!scene) return;
      const featureId = scene.get('featureId');
      const own = scene.delete('featureId') as I.ImmutableOf<ApiOrderableScene>;
      scenesByFeature.set(featureId, (scenesByFeature.get(featureId) ?? I.List()).push(own));
    });
  }
  return {asked: new Set(keys.map(sceneKeyOf)), scenesByFeature};
}

export interface FeatureLookup {
  asked: ReadonlySet<number>;
  /** Archived or deleted properties are absent. */
  featuresById: ReadonlyMap<number, I.ImmutableOf<ApiFeature>>;
}

/** Properties by id, through the batch endpoint: one request per hundred, not one per property. */
export async function lookUpFeatures(
  targets: readonly {featureCollectionId: number; featureId: number}[]
): Promise<FeatureLookup> {
  const idsByCollection = new Map<number, number[]>();
  for (const {featureCollectionId, featureId} of targets) {
    idsByCollection.set(featureCollectionId, [
      ...(idsByCollection.get(featureCollectionId) ?? []),
      featureId,
    ]);
  }
  const requests: Promise<I.List<I.ImmutableOf<ApiFeature>>>[] = [];
  for (const [featureCollectionId, ids] of idsByCollection) {
    for (let start = 0; start < ids.length; start += FEATURES_PER_REQUEST) {
      const chunk = ids.slice(start, start + FEATURES_PER_REQUEST);
      requests.push(
        api.featureCollections
          .features(featureCollectionId)
          .batchFetch({id: chunk.map(String), perPage: FEATURES_PER_REQUEST})
          .then((response) => response.get('data'))
      );
    }
  }
  const featuresById = new Map<number, I.ImmutableOf<ApiFeature>>();
  for (const features of await Promise.all(requests)) {
    features.forEach((feature) => {
      if (feature) featuresById.set(feature.get('id'), feature);
    });
  }
  return {asked: new Set(targets.map(({featureId}) => featureId)), featuresById};
}
